import React, { useState } from 'react';
import { Alert, Box, Collapse, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { HelpOutline, ExpandMore, ExpandLess } from '@mui/icons-material';

/**
 * Small German help affordances for the Broker Hub — an info tooltip and a
 * collapsible "So funktioniert's" legend. Kept in one place so wording stays
 * consistent and non-technical (this is Christian's tool).
 */

/** Inline info-icon with a German tooltip. */
export const InfoHint: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip title={text} arrow>
    <HelpOutline
      sx={{ fontSize: 16, ml: 0.5, opacity: 0.6, cursor: 'help', verticalAlign: 'text-bottom' }}
    />
  </Tooltip>
);

/** Collapsible German how-to box. `storageKey` remembers the open/closed state. */
export const HelpLegend: React.FC<{
  title?: string;
  items: Array<{ term: string; desc: string }>;
  storageKey?: string;
  defaultOpen?: boolean;
}> = ({ title = "So funktioniert's", items, storageKey, defaultOpen = false }) => {
  const [open, setOpen] = useState(() => {
    if (storageKey) {
      const v = localStorage.getItem(`bh_help_${storageKey}`);
      if (v !== null) return v === '1';
    }
    return defaultOpen;
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (storageKey) localStorage.setItem(`bh_help_${storageKey}`, next ? '1' : '0');
  };
  return (
    <Alert
      severity="info"
      icon={<HelpOutline />}
      sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography fontWeight="bold" variant="body2">
          {title}
        </Typography>
        <IconButton size="small" onClick={toggle}>
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
          {items.map((it) => (
            <li key={it.term}>
              <Typography variant="body2" component="span">
                <strong>{it.term}:</strong> {it.desc}
              </Typography>
            </li>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
};

export const MATRIX_HELP = [
  {
    term: 'Matrix',
    desc: 'Jede Zeile ist ein Broker, jede Spalte ein Portal. Klick auf eine Zelle öffnet den Profil-Editor.',
  },
  { term: 'Live / Entwurf / Offline', desc: 'Status des Profils auf dem jeweiligen Portal.' },
  {
    term: 'Rotes Wolken-Symbol',
    desc: 'Status ist Live, aber die Seite ist NICHT ausgespielt — sie steht nicht auf dem Portal. Profil öffnen und „Deploy" klicken.',
  },
  {
    term: 'Blaues Sync-Symbol',
    desc: 'Es gibt gespeicherte Änderungen, die noch nicht ausgespielt wurden. „Deploy" klicken, damit sie live gehen.',
  },
  {
    term: 'Neues Profil',
    desc: 'Legt ein Broker-Profil für ein Portal an — leer, als Kopie der Master-Version oder eines anderen Portals.',
  },
  {
    term: 'Design (Basis-CSS)',
    desc: 'Zentrales Aussehen aller Profile — einmal bearbeiten, auf alle Portale ausspielen. Pro Portal optional eine Marken-Anpassung (Farbe/Schrift).',
  },
  {
    term: 'Diskrepanzen',
    desc: 'Vom Import erkannte Unstimmigkeiten (z. B. abweichende Affiliate-Links) zum Abarbeiten.',
  },
  {
    term: 'Ausspielen (Broker-Zeile)',
    desc: 'Bringt Risikohinweis und Affiliate-Link dieses Brokers auf ALLE Portale. Der Hub prüft vorher jedes Portal und fängt gar nicht erst an, wenn eines scheitern würde — so entsteht kein halb fertiger Stand.',
  },
  {
    term: '„N Portal(e) offen"',
    desc: 'Dieser Broker zeigt nicht überall dasselbe: irgendwo fehlt die letzte Änderung. Auf „Ausspielen" klicken, der Dialog nennt jedes Portal einzeln.',
  },
  {
    term: 'Risikohinweis ändern (z. B. 80 % auf 81 %)',
    desc: 'Compliance öffnen, Broker wählen, Text einmal ändern, speichern. Danach in der Broker-Zeile „Ausspielen". Nicht im Profil-Text und nicht in der Karte suchen — die holen sich den Wert von dort.',
  },
  {
    term: 'Affiliate-Link ändern',
    desc: 'Der Link ist PRO PORTAL verschieden. Profil des Portals öffnen, Feld „Affiliate-Link", speichern — dann „Ausspielen". Karte und Seite bekommen ihn automatisch.',
  },
];

export const EDITOR_HELP = [
  {
    term: 'Vorschau',
    desc: 'Zeigt das Profil live mit dem echten Portal-Design, während du tippst. Rechts neben dem Editor.',
  },
  { term: 'HTML / CSS', desc: 'Der Inhalt als Code. Farbige Hervorhebung + Zeilennummern.' },
  {
    term: 'Speichern',
    desc: 'Legt eine neue Version an (jederzeit wiederherstellbar). <script>-Tags werden dabei automatisch entfernt.',
  },
  {
    term: 'Deploy',
    desc: 'Spielt den gespeicherten Stand live auf das Portal aus. Vorher speichern. Bei „Offline"/„Entwurf" nimmt der Deploy die Seite vom Netz (mit Rückfrage) und stellt das Original wieder her.',
  },
  {
    term: 'Seitentitel (SEO)',
    desc: 'Optionaler Titel für die Seite (title/og:title). Wird beim Deploy gesetzt. Leer = bestehender Titel bleibt.',
  },
];

export const NEW_PROFILE_HELP =
  'Wähle einen Broker (oder lege einen neuen an), das Portal und woher der Start-Inhalt kommt: leer, Master-Version oder Kopie eines anderen Portals.';

export const DESIGN_HELP = [
  {
    term: 'Basis-CSS',
    desc: 'Das gemeinsame Aussehen aller Broker-Profile über alle Portale. Einmal ändern → auf alle ausspielen.',
  },
  {
    term: 'Portal-Anpassung',
    desc: 'Kleine markenspezifische Überschreibung pro Portal (z. B. Akzentfarbe, Schriftart). Optional.',
  },
  {
    term: 'Ausspielen',
    desc: 'Schreibt Basis + Portal-Anpassung als eine Datei auf das Portal — alle Profile dort übernehmen es sofort.',
  },
];

export const helpDocLink = (
  <Typography variant="caption" color="text.secondary">
    Fragen? Siehe{' '}
    <Link href="#" underline="hover">
      Broker-Hub Kurzanleitung
    </Link>
    .
  </Typography>
);
